import { CloseButton } from 'components/button-close/close-button';
import Loading from 'components/loading/loading';
import _ from 'lodash';
import { HttpRequestStatus } from 'model/enums/httpRequestStatus';
import { SeriesType } from 'model/enums/series-type';
import { FundSeriesAndInvestor } from 'model/series';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router';
import { Modal } from 'reactstrap';
import { getAvailableYearsByIssuerIdResetState } from 'reducer/financial-statement-month/get-available-years-by-issuer-id/actions';
import { useGetAllSecondaryAcessSeriesState, useRootDispatch } from 'reducer/hooks';
import { getIncomeStatementAvailableYearsResetState } from 'reducer/income-statement/get-available-years-by-issuer-id/actions';
import { useFundAndSeriesContext } from 'shared/fund-and-series-selection/fund-and-series-selection-context';
import useFilterUtils from 'shared/hooks/use-filter-utils';
import { ParticipantsBaseTabLinks } from 'shared/menu/participant-header/participant-tab-menu';
import SelectAllSecondaryAccessCard from './components/select-all-card/select-all-secondary-access-card';

import { HeaderVisualizationType } from 'model/enums/header-visualization-type';
import { getAllSecondaryAccessSeriesRequest, getAllSecondaryAccessSeriesResetState } from 'reducer/secondary-access/get-all-secondary-access-series/actions';
import './select-all-secondary-access-modal.scss';

interface SelectAllSecondaryAccessModalProps {
    handleClose: () => void;
    baseUrl?: string;
}

const SelectAllSecondaryAccessModal = (props: SelectAllSecondaryAccessModalProps) => {
    const { handleClose, baseUrl } = props;
    const { t } = useTranslation();

    const { allSecondaryAccessSeries, status } = useGetAllSecondaryAcessSeriesState();
    const { buildSearchParams } = useFilterUtils();
    const { handleSelectFundAndSeries } = useFundAndSeriesContext();

    const dispatch = useRootDispatch();
    const history = useHistory();
    const { url } = useRouteMatch();

    const participantTabLinks = _.values(ParticipantsBaseTabLinks);
    const existsByParticipantsTabLinks: boolean = participantTabLinks.includes(url);

    const getUrlToRedirect = useCallback(
        (seriesType: string): string => {
            if (seriesType !== SeriesType.RESULT && !existsByParticipantsTabLinks) {
                return ParticipantsBaseTabLinks.GENERAL;
            }

            return baseUrl ?? url;
        },
        [baseUrl, url, existsByParticipantsTabLinks]
    );

    const handleFundAndSeries = useCallback(
        (fundAndSeries: FundSeriesAndInvestor) => {

            const fundSeriesAndInvestor: FundSeriesAndInvestor = {
                fundId: fundAndSeries.fundId,
                fundName: fundAndSeries.fundName,
                fundType: fundAndSeries.fundType,
                seriesId: fundAndSeries.seriesId,
                seriesName: fundAndSeries.seriesName,
                seriesType: fundAndSeries.seriesType,
                personId: fundAndSeries?.personId,
                personName: fundAndSeries?.personName,
                headerVisualizationType: HeaderVisualizationType.SECONDARY_ACCESS
            };

            handleSelectFundAndSeries(fundSeriesAndInvestor);
            history.push({
                pathname: getUrlToRedirect(fundSeriesAndInvestor.seriesType),
                search: buildSearchParams(fundSeriesAndInvestor)
            });

            dispatch(getAvailableYearsByIssuerIdResetState());
            dispatch(getIncomeStatementAvailableYearsResetState());
            handleClose();
        },
        [handleSelectFundAndSeries, history, buildSearchParams, handleClose, getUrlToRedirect, dispatch]
    );

    useEffect(() => {
        dispatch(getAllSecondaryAccessSeriesRequest());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(getAllSecondaryAccessSeriesResetState());
        };
    }, [dispatch]);

    const isLoading = status === HttpRequestStatus.ONGOING;

    return (
        <Modal isOpen size="xl" modalClassName="select-all-secondary-access-modal" toggle={handleClose}>
            <div className="select-all-secondary-access-modal--container">
                <div className="select-all-secondary-access-modal--header">
                    <label className="select-all-secondary-access-modal--header-title">{t('secondary-access-page.modal.title')}</label>
                    <CloseButton onClick={handleClose} />
                </div>
            </div>
            <div className="select-all-secondary-access-modal--list">
                {isLoading ? (
                    <Loading />
                ) : (
                    <>
                        {allSecondaryAccessSeries?.map((it, index) => (
                            <SelectAllSecondaryAccessCard key={it.seriesId} fundAndSeries={it} action={() => handleFundAndSeries(it)} dataCy={`button#select-series-${index}`} />
                        ))}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default SelectAllSecondaryAccessModal;
